import { createTimeline, stagger } from '../../vendor/anime.esm.js';
import { DURATION, EASE, STAGGER, SPRING_PRESETS, resolveDuration, resolveStagger, BLUR } from '../motion-tokens.js';

/**
 * BENTO — mosaico asimétrico (v1 mosaico · v2 editorial · v3 glass).
 *
 * Las tres variantes comparten UN render (bento.js es CSS-driven), así que comparten una
 * sola coreografía: se registra con la clave sin variante `'bento'` y la cascada de
 * `getVariantAnimation` la sirve a v1/v2/v3 (mismo patrón que form/menu/services).
 *
 * El mosaico NO es una fila: las piezas tienen tamaños distintos (lg 2x2, wide, tall, std)
 * y `grid-auto-flow: dense` las recoloca. Por eso el orden de entrada se calcula sobre los
 * OFFSETS REALES del DOM (como galleryV1) → cascada diagonal que respeta la retícula tal y
 * como se ve, sea cual sea el breakpoint. Jerarquía: primero asientan las piezas grandes
 * (el ancla visual del bento), después el resto, y al final hace pop el medallón/número.
 *
 * Las mini-escenas de los tiles (`.bento-scene`: counter, terminal, ticker…) las gobierna
 * `animations/bento-scenes-anim.js` con su propio IntersectionObserver: aquí NO se tocan
 * (targets distintos → sin writes solapados, gotcha nº18).
 */
export const bentoMosaic = (el) => {
    const tiles = [...el.querySelectorAll('.bento-tile')];
    if (!tiles.length) return null;

    const grandes = tiles.filter(t => t.classList.contains('bento-lg'));
    const resto = tiles.filter(t => !t.classList.contains('bento-lg'));
    // Medallón de icono (v1/v3) o número display (v2): el mismo hueco, un solo target.
    const medias = el.querySelectorAll('.bento-ico, .bento-num');

    // Estado inicial forzado (previene el flash de contenido antes del primer frame).
    tiles.forEach(t => {
        t.style.opacity = '0';
        t.style.transform = 'translateY(26px) scale(0.95)';
        t.style.filter = BLUR.normal;
    });
    medias.forEach(m => { m.style.opacity = '0'; m.style.transform = 'scale(0.72)'; });

    // Retícula real medida en el DOM (columnas/filas distintas por breakpoint).
    const columnas = new Set();
    const filas = new Set();
    resto.forEach(t => { columnas.add(t.offsetLeft); filas.add(t.offsetTop); });
    const grid = [columnas.size || 1, filas.size || 1];

    const tl = createTimeline({ defaults: { ease: EASE.refined } });

    if (grandes.length) tl.add(grandes, {
        opacity:    [0, 1],
        translateY: [26, 0],
        scale:      [0.95, 1],
        filter:     [BLUR.normal, BLUR.none],
        delay:      stagger(resolveStagger(STAGGER.fast)),
        duration:   resolveDuration(DURATION.slow),
        ease:       SPRING_PRESETS.deluxe,
    }, 0);

    if (resto.length) tl.add(resto, {
        opacity:    [0, 1],
        translateY: [26, 0],
        scale:      [0.95, 1],
        filter:     [BLUR.light, BLUR.none],
        delay:      stagger(resolveStagger(70), { grid, from: 'first' }),
        duration:   resolveDuration(DURATION.normal),
        ease:       SPRING_PRESETS.deluxe,
    }, grandes.length ? '-=900' : 0);

    if (medias.length) tl.add(medias, {
        opacity:  [0, 1],
        scale:    [0.72, 1],
        delay:    stagger(resolveStagger(STAGGER.fast)),
        duration: resolveDuration(DURATION.fast),
        ease:     SPRING_PRESETS.magnetic,
    }, '-=700');

    return tl;
};
